import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import Header from '@/components/Header'
import { useNavigate } from 'react-router-dom'

export default function Home() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const navigate = useNavigate()
  const [ecgPattern, setEcgPattern] = useState<string>('normal')
  const [heartRate, setHeartRate] = useState<number>(72)
  const [respRate, setRespRate] = useState<number>(16)
  const [ecgEvaluation, setEcgEvaluation] = useState<string>('Ritmo Sinusal Normal')

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Configurações do ECG
    const isMobile = window.innerWidth < 768
    const numLeads = isMobile ? 2 : 5 // Reduzido para otimização: 2 derivações no mobile, 5 no desktop
    const leadHeight = isMobile ? canvas.height / 4 : canvas.height / 8 // Altura de cada derivação
    const leadSpacing = isMobile ? leadHeight * 1.2 : leadHeight * 1.3 // Espaçamento entre derivações
    const leadStartY = isMobile ? leadHeight : leadHeight * 1.5 // Posição Y inicial
    
    // Parâmetros do ECG
    const ecgSpeed = isMobile ? 0.6 : 1.0 // Velocidade reduzida ainda mais para melhor desempenho
    const ecgAmplitude = leadHeight * 0.25 // Amplitude reduzida para ficar mais sutil
    const gridSize = 20 // Tamanho da grade em pixels

    // Controladores de padrão de ECG e intervalos de tempo
    let patternTimer = 0
    let currentHR = heartRate
    const patternDuration = 15000 // 15 segundos por padrão
    
    // Ciclo de padrões de ECG (removendo os BAVs como solicitado)
    const patterns = ['normal', 'taquicardia', 'bradicardia', 'fibrilacao', 'isquemia', 'normal']
    
    // Timer para alternar automaticamente entre os padrões
    const switchPattern = () => {
      const currentIndex = patterns.indexOf(ecgPattern)
      const nextIndex = (currentIndex + 1) % patterns.length
      setEcgPattern(patterns[nextIndex])
      
      // Ajustar frequência cardíaca e avaliação com base no padrão
      switch (patterns[nextIndex]) {
        case 'taquicardia':
          setHeartRate(120)
          setRespRate(22)
          setEcgEvaluation('Taquicardia Sinusal')
          break
        case 'bradicardia':
          setHeartRate(42)
          setRespRate(14)
          setEcgEvaluation('Bradicardia Sinusal')
          break
        case 'fibrilacao':
          setHeartRate(150)
          setRespRate(24)
          setEcgEvaluation('Fibrilação Atrial')
          break
        case 'isquemia':
          setHeartRate(85)
          setRespRate(18)
          setEcgEvaluation('Isquemia Miocárdica')
          break
        default:
          setHeartRate(72)
          setRespRate(16)
          setEcgEvaluation('Ritmo Sinusal Normal')
      }
    }
    
    // Configurar o timer para alternar padrões
    const patternInterval = setInterval(switchPattern, patternDuration)
    
    // Tempo para um ciclo completo de ECG (ms)
    const calculateCycleDuration = (hr: number) => 60000 / hr // 60000ms (1 min) / FC

    // Array para armazenar os dados de cada derivação
    const leadData: {
      name: string; 
      points: number[]; 
      position: number;
      color: string;
    }[] = [];

    // Nomes das derivações de ECG
    const leadNames = ["DI", "DII", "DIII", "aVR", "aVL", "aVF", "V1", "V2", "V3", "V4", "V5", "V6"];
    
    // Cores diferentes para cada derivação (mais suaves e uniformes)
    const leadColors = [
      'rgba(16, 185, 129, 0.6)',  // Verde médio mais transparente
      'rgba(4, 120, 87, 0.6)',    // Verde escuro mais transparente
      'rgba(5, 150, 105, 0.6)',   // Verde esmeralda mais transparente
      'rgba(6, 95, 70, 0.6)',     // Verde escuro mais transparente
      'rgba(52, 211, 153, 0.6)',  // Verde claro mais transparente
      'rgba(16, 185, 129, 0.6)',  // Verde médio mais transparente
    ];

    // Função para gerar pontos de ECG conforme o padrão atual - OTIMIZADO
    const generateECGPoints = (derivationIndex: number, pattern: string, amplitude: number, cycleWidth: number) => {
      // Otimização: reduzir número de pontos calculados
      const pointReductionFactor = isMobile ? 2 : 1; // No mobile, calculamos metade dos pontos
      const points: number[] = [];
      // Menos ciclos para melhor desempenho
      const cycles = Math.ceil(canvas.width * 2 / cycleWidth) + 1;
  
      // Propriedades específicas para cada derivação
      const leadVariation = {
        'DI': { amplitude: 0.8, inverted: false, stElevation: 0 },
        'DII': { amplitude: 1.0, inverted: false, stElevation: 0 },
        'DIII': { amplitude: 0.7, inverted: false, stElevation: 0 },
        'aVR': { amplitude: 0.6, inverted: true, stElevation: 0 },
        'aVL': { amplitude: 0.6, inverted: false, stElevation: 0 },
        'aVF': { amplitude: 0.9, inverted: false, stElevation: 0 }
      };
      
      // Ajustes com base na derivação atual
      const leadName = leadNames[derivationIndex];
      const leadAmp = leadVariation[leadName as keyof typeof leadVariation]?.amplitude || 1.0;
      const isInverted = leadVariation[leadName as keyof typeof leadVariation]?.inverted || false;
      let stElevation = leadVariation[leadName as keyof typeof leadVariation]?.stElevation || 0;
      
      // Modificar características conforme o padrão
      let segmentTRatio = 0.16; // % do ciclo para segmento ST
      let pAmplitude = 0.25;    // % da amplitude R
      let tAmplitude = 0.3;     // % da amplitude R
      let qrsWidth = 0.05;      // % do ciclo para QRS
      let stShape = 0;          // Forma do segmento ST
      
      switch (pattern) {          
        case 'taquicardia':
          pAmplitude = 0.2;
          tAmplitude = 0.25;
          segmentTRatio = 0.1; // ST mais curto na taquicardia
          break;
          
        case 'bradicardia':
          pAmplitude = 0.3;
          tAmplitude = 0.35;
          segmentTRatio = 0.25; // ST mais longo na bradicardia
          break;
          
        case 'fibrilacao':
          pAmplitude = 0.05; // Ondas P quase imperceptíveis
          tAmplitude = 0.2;
          qrsWidth = 0.03;
          
          // Adicionar "tremor" na linha base
          for (let c = 0; c < cycles; c++) {
            for (let i = 0; i < cycleWidth; i += pointReductionFactor) {
              // Fibrilação: linha irregular com pequenas ondulações rápidas
              const noiseAmplitude = Math.random() * 0.15 * amplitude;
              const noise = Math.sin(i * 0.8) * noiseAmplitude + Math.sin(i * 1.2) * noiseAmplitude;
              
              // Complexos QRS ocasionais e irregulares
              if (i % Math.floor(cycleWidth / 3) === 0 && Math.random() > 0.4) {
                // QRS irregular
                for (let j = 0; j < cycleWidth * 0.07; j += pointReductionFactor) {
                  const qrs = Math.sin(j / (cycleWidth * 0.07) * Math.PI) * amplitude * leadAmp * (isInverted ? 1 : -1);
                  points.push(qrs);
                }
                i += Math.floor(cycleWidth * 0.07);
              } else {
                points.push(noise);
              }
            }
          }
          
          return points;
          
        case 'isquemia':
          // Ajustar elevação ST conforme derivação para isquemia
          if (leadName === 'DII' || leadName === 'DIII' || leadName === 'aVF') {
            stElevation = 0.2; // Elevação do segmento ST
            stShape = -1;      // Segmento ST côncavo para cima
          } else if (leadName === 'V1' || leadName === 'V2' || leadName === 'V3') {
            stElevation = 0.25; // Maior elevação ST nas precordiais
            stShape = -1;
          } else if (leadName === 'aVR') {
            stElevation = -0.2; // Depressão do segmento ST
            stShape = 1;
          }
          
          tAmplitude = 0.2; // Onda T menor ou invertida
          break;
          
        default: // 'normal'
          // Valores padrão já definidos
          break;
      }
      
      // Gerar ciclos de ECG - OTIMIZADO
      for (let c = 0; c < cycles; c++) {
        // Percentuais do ciclo para cada componente
        const pWaveWidth = cycleWidth * 0.08;
        const prSegmentWidth = cycleWidth * 0.06;
        const qrsComplexWidth = cycleWidth * qrsWidth;
        const stSegmentWidth = cycleWidth * segmentTRatio;
        const tWaveWidth = cycleWidth * 0.12;
        const baselineWidth = cycleWidth - (pWaveWidth + prSegmentWidth + qrsComplexWidth + stSegmentWidth + tWaveWidth);
        
        // Linha base
        for (let i = 0; i < baselineWidth; i += pointReductionFactor) {
          points.push(0);
        }
        
        // Onda P
        for (let i = 0; i < pWaveWidth; i += pointReductionFactor) {
          const pHeight = -Math.sin((i / pWaveWidth) * Math.PI) * amplitude * pAmplitude * leadAmp;
          points.push(isInverted ? -pHeight : pHeight);
        }
        
        // Segmento PR
        for (let i = 0; i < prSegmentWidth; i += pointReductionFactor) {
          points.push(0);
        }
        
        // Complexo QRS
        // Onda Q
        const qWidth = qrsComplexWidth * 0.2;
        for (let i = 0; i < qWidth; i += pointReductionFactor) {
          const qHeight = Math.sin((i / qWidth) * Math.PI) * amplitude * 0.2 * leadAmp;
          points.push(isInverted ? -qHeight : qHeight);
        }
        
        // Onda R
        const rWidth = qrsComplexWidth * 0.3;
        for (let i = 0; i < rWidth; i += pointReductionFactor) {
          const rHeight = -Math.sin((i / rWidth) * Math.PI) * amplitude * leadAmp;
          points.push(isInverted ? -rHeight : rHeight);
        }
        
        // Onda S
        const sWidth = qrsComplexWidth * 0.5;
        for (let i = 0; i < sWidth; i += pointReductionFactor) {
          const sHeight = Math.sin((i / sWidth) * Math.PI) * amplitude * 0.3 * leadAmp;
          points.push(isInverted ? -sHeight : sHeight);
        }
        
        // Segmento ST (podendo ter elevação/depressão em alguns padrões)
        for (let i = 0; i < stSegmentWidth; i += pointReductionFactor) {
          let stHeight = amplitude * stElevation * leadAmp;
          
          // Adicionar curvatura ao segmento ST quando indicado
          if (stShape !== 0) {
            const normalizedPos = i / stSegmentWidth;
            const curve = Math.sin(normalizedPos * Math.PI) * 0.05 * amplitude;
            stHeight += stShape * curve;
          }
          
          points.push(isInverted ? -stHeight : stHeight);
        }
        
        // Onda T
        for (let i = 0; i < tWaveWidth; i += pointReductionFactor) {
          let tHeight = -Math.sin((i / tWaveWidth) * Math.PI) * amplitude * tAmplitude * leadAmp;
          
          // Inverter T na isquemia em algumas derivações
          if (pattern === 'isquemia' && (leadName === 'DI' || leadName === 'aVL' || leadName === 'V5' || leadName === 'V6')) {
            tHeight *= -1;
          }
          
          points.push(isInverted ? -tHeight : tHeight);
        }
      }
      
      return points;
    };

    // Gerar ECG para cada derivação
    const generateAllLeads = () => {
      leadData.length = 0; // Limpar dados anteriores
      
      const cycleDuration = calculateCycleDuration(currentHR);
      // Convertendo duração de ms para pixels, considerando a velocidade do ECG
      const cycleWidth = (cycleDuration / 1000) * 110; // 25mm/s = ~100px/s em escala padrão de ECG
      
      for (let i = 0; i < numLeads; i++) {
        const points = generateECGPoints(i, ecgPattern, ecgAmplitude, cycleWidth);
        
        // Adicionar dados da derivação ao array
        leadData.push({
          name: leadNames[i],
          points: points,
          position: 0,
          color: leadColors[i % leadColors.length]
        });
      }
    };
    
    generateAllLeads();
    
    // Alterar ECG quando o padrão mudar
    let lastPattern = ecgPattern;
    let lastHR = currentHR;

    // Grade do ECG com cores mais leves - OTIMIZADA
    const drawECGGrid = () => {
      // Otimização: desenhar grade em intervalos maiores
      const gridDensity = isMobile ? 2 : 1; // Menos linhas no mobile
      
      // Desenhar fundo claro para o papel quadriculado
      ctx.fillStyle = 'rgba(250, 250, 250, 0.9)'; // Fundo quase branco
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Grade maior (5x5 mm) - linhas mais visíveis
      ctx.strokeStyle = 'rgba(200, 230, 220, 0.8)'; // Verde muito claro
      ctx.lineWidth = 0.6;
      
      // Grid vertical principal
      for (let x = 0; x < canvas.width; x += gridSize * 5 * gridDensity) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      // Grid horizontal principal
      for (let y = 0; y < canvas.height; y += gridSize * 5 * gridDensity) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Grade menor (1x1 mm) - linhas mais sutis
      ctx.strokeStyle = 'rgba(220, 240, 230, 0.6)'; // Verde ainda mais claro
      ctx.lineWidth = 0.3;
      
      // No mobile, reduzir a densidade da grade menor para melhor desempenho
      const smallGridDensity = isMobile ? 3 : 1;
      
      for (let x = 0; x < canvas.width; x += gridSize * smallGridDensity) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      for (let y = 0; y < canvas.height; y += gridSize * smallGridDensity) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    };

    // Desenhar cada derivação de ECG - OTIMIZADA
    const drawECGLeads = () => {
      for (let i = 0; i < leadData.length; i++) {
        const lead = leadData[i];
        const yBase = leadStartY + (i * leadSpacing);
        
        // Desenhar nome da derivação (mais visível)
        ctx.font = isMobile ? '12px monospace' : '14px monospace';
        ctx.fillStyle = 'rgba(6, 78, 59, 0.9)'; // Verde escuro mais visível
        ctx.fillText(lead.name, 10, yBase - leadHeight * 0.4);
        
        // Desenhar traçado do ECG
        ctx.strokeStyle = lead.color;
        ctx.lineWidth = 1.5; // Ligeiramente mais grosso para destacar
        ctx.beginPath();
        
        const points = lead.points;
        const position = Math.floor(lead.position);
        const pointsToShow = Math.min(canvas.width, points.length);
        const skipPoints = isMobile ? 2 : 1; // No mobile, renderizar pontos alternados para melhor desempenho
        
        for (let x = 0; x < pointsToShow; x += skipPoints) {
          const idx = (x + position) % points.length;
          const y = yBase + points[idx];
          
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
          
          // Desenhar o ponto cursor no final do traçado (apenas se for o último ponto visível)
          if (x >= pointsToShow - skipPoints) {
            ctx.stroke(); // Finalizar a linha atual
            
            // Desenhar círculo no ponto final (menor)
            ctx.beginPath();
            ctx.fillStyle = lead.color.replace('0.6)', '0.9)'); // Mais visível que a linha
            ctx.arc(x, y, 3, 0, Math.PI * 2);
            ctx.fill();
            
            // Adicionar efeito de brilho (menor)
            ctx.beginPath();
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.arc(x, y, 1.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        
        // Atualizar a posição para o próximo frame
        lead.position = (lead.position + ecgSpeed) % (points.length - canvas.width);
        
        // Efeito de fade para a linha ECG mais suave e mais longo
        const gradient = ctx.createLinearGradient(canvas.width - 80, 0, canvas.width, 0);
        gradient.addColorStop(0, lead.color);
        gradient.addColorStop(1, 'rgba(30, 64, 175, 0)');
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        
        // Desenhar apenas a parte final com gradiente (estendido)
        const fadeStartX = canvas.width - 80;
        for (let x = fadeStartX; x < pointsToShow; x += skipPoints) {
          const idx = (x + position) % points.length;
          const y = yBase + points[idx];
          
          if (x === fadeStartX) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }
    };
    
    // Desenhar monitores de Frequência Cardíaca e Respiratória - CORRIGIDO
    const drawVitalSigns = () => {
      const width = 160;
      const height = 110; // Reduzido para evitar ultrapassar limites
      // Canto direito, um pouco mais abaixo no desktop, mas logo abaixo do header
      const x = canvas.width - width - 20;
      const headerHeight = isMobile ? 56 : 64; // Altura aproximada do header em pixels
      const y = headerHeight + 8; // Logo abaixo do header
      
      // Fundo do monitor
      ctx.fillStyle = 'rgba(0, 26, 18, 0.5)'; // Verde escuro mais opaco para melhor visibilidade
      ctx.beginPath();
      ctx.roundRect(x, y, width, height, 8);
      ctx.fill();

      // Adicionar borda sutil para destacar
      ctx.strokeStyle = 'rgba(128, 255, 219, 0.4)';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Textos e valores
      ctx.font = 'bold 14px monospace';
      
      // Título
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText('Sinais Vitais', x + 40, y + 20);
      
      // Linha separadora
      ctx.strokeStyle = 'rgba(128, 255, 219, 0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + 15, y + 30);
      ctx.lineTo(x + width - 15, y + 30);
      ctx.stroke();
      
      // FC - Frequência Cardíaca
      ctx.fillStyle = 'rgba(233, 213, 22, 1)'; // Amarelo mais vibrante
      ctx.font = 'bold 18px monospace';
      ctx.fillText('FC:', x + 20, y + 55);
      ctx.fillText(`${heartRate} bpm`, x + 60, y + 55);
      
      // FR - Frequência Respiratória
      ctx.fillStyle = 'rgba(80, 255, 178, 1)'; // Verde mais vibrante
      ctx.font = 'bold 18px monospace';
      ctx.fillText('FR:', x + 20, y + 80);
      ctx.fillText(`${respRate} rpm`, x + 60, y + 80);
      
      // Avaliação do ECG - Ajustado para não ultrapassar limites
      const evalText = ecgEvaluation;
      ctx.fillStyle = 'rgba(255, 255, 255, 1)';
      ctx.font = 'bold 12px monospace';
      
      // Verificar se o texto é muito longo
      const availableWidth = width - 30;
      const truncateText = (text: string, maxWidth: number) => {
        if (ctx.measureText(text).width <= maxWidth) return text;
        
        // Truncar o texto se necessário
        for (let i = text.length - 1; i > 0; i--) {
          const truncated = text.substring(0, i) + '...';
          if (ctx.measureText(truncated).width <= maxWidth) {
            return truncated;
          }
        }
        return text.substring(0, 5) + '...';
      };
      
      ctx.fillText('Avaliação:', x + 20, y + 100);
      ctx.fillText(truncateText(evalText, availableWidth - ctx.measureText('Avaliação: ').width), 
                   x + 100, y + 100);
    };

    // Partículas de fundo (ainda mais reduzidas para otimização)
    const particles: Array<{x: number, y: number, size: number, speedX: number, speedY: number, opacity: number}> = [];
    const particleCount = isMobile ? 4 : 8; // Reduzido para melhor desempenho
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.5, // Ligeiramente menores
        speedX: (Math.random() * 0.4 - 0.2) * (isMobile ? 0.15 : 0.4), // Mais lentas
        speedY: (Math.random() * 0.4 - 0.2) * (isMobile ? 0.15 : 0.4), // Mais lentas
        opacity: Math.random() * 0.12 + 0.03 // Mais transparentes
      });
    }

    // Desenhar partículas para efeito visual - OTIMIZADA
    const drawParticles = () => {
      // Pular frames para melhor desempenho
      if (Math.random() > 0.6 && isMobile) return; // 40% de chance de pular um frame no mobile

      particles.forEach((particle, i) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        ctx.beginPath();
        ctx.fillStyle = `rgba(6, 78, 59, ${particle.opacity * 0.7})`; // Verde escuro mais transparente
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();

        // Conectar partículas próximas com linhas ainda mais sutis
        // Otimização: no mobile, conectar apenas com as próximas 2 partículas
        const maxConnections = isMobile ? Math.min(2, particles.length - i - 1) : particles.length - i - 1;
        for (let j = i + 1; j <= i + maxConnections; j++) {
          const p2 = particles[j % particles.length];
          const distance = Math.sqrt(Math.pow(particle.x - p2.x, 2) + Math.pow(particle.y - p2.y, 2));
          
          if (distance < 70) { // Distância reduzida para menos linhas
            ctx.beginPath();
            ctx.strokeStyle = `rgba(6, 78, 59, ${0.02 * (1 - distance / 70)})`; // Verde escuro mais transparente
            ctx.lineWidth = 0.15; // Muito fino
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      });
    };

    // Função principal de animação - OTIMIZADA
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Verificar se o padrão ou frequência cardíaca mudou
      if (lastPattern !== ecgPattern || lastHR !== currentHR) {
        lastPattern = ecgPattern;
        lastHR = currentHR;
        generateAllLeads();
      }
      
      // Incrementar o timer e atualizar HR atual
      patternTimer += 16; // ~60fps
      currentHR = heartRate;
      
      // Otimização: desenhar grade com menos frequência
      drawECGGrid();
      drawParticles();
      drawECGLeads();
      drawVitalSigns();
      
      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      clearInterval(patternInterval);
    };
  }, [ecgPattern, heartRate, respRate, ecgEvaluation]);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradientes animados em camadas */}
      <div className="absolute inset-0">
        {/* Gradiente base */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white to-white" />
        
        {/* Gradiente radial pulsante - mais sutil */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#064e3b]/5 via-transparent to-transparent sm:animate-pulse-slow animate-pulse-slower" />
        
        {/* Gradiente diagonal animado - mais sutil */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#064e3b]/2 via-transparent to-[#064e3b]/5 sm:animate-gradient-slow animate-gradient-slower" />
        
        {/* Vinheta suave nas bordas - mais sutil */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_70%,_rgba(0,0,0,0.05)_100%)]" />
      </div>
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.5 }} // Reduzido para ficar bem mais transparente
      />

      <div className="relative">
        <Header showAuthButtons />

        <div className="container flex flex-col items-center min-h-screen px-4">
          {/* Conteúdo Principal */}
          <div className="flex flex-col items-center justify-center flex-grow gap-3 sm:gap-5 py-6 sm:py-8 mt-14 sm:mt-16 text-center px-4 sm:px-0">
            {/* Social Proof + Urgência */}
            <div className="flex items-center gap-2 text-sm sm:text-base font-medium text-[#064e3b] animate-pulse">
              <span className="inline-block w-2 h-2 rounded-full bg-[#064e3b]" />
              +2.000 médicos já utilizam
            </div>

            {/* Headline mais focada em benefício */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl text-center">
              <span className="text-[#064e3b]">Interprete ECG com IA</span>{' '}
              <br className="hidden sm:block" />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#064e3b] to-[#065f46]">
                em menos de 15 segundos
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-text-200 max-w-2xl text-center px-4 sm:px-0">
              Tenha mais segurança nas suas decisões com análises profundas e instantâneas. 
              <strong className="text-[#064e3b]"> 100% gratuito.</strong>
            </p>

            {/* CTA Principal com mais destaque */}
            <div className="mt-3 sm:mt-5 w-full sm:w-auto px-4 sm:px-0">
              <button 
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto group relative inline-flex items-center justify-center px-8 sm:px-12 py-3 sm:py-4 text-lg sm:text-xl font-bold text-white bg-[#064e3b] rounded-2xl overflow-hidden shadow-2xl hover:shadow-[0_8px_25px_-5px_rgba(6,78,59,0.4)] transition-all duration-300 hover:scale-105"
                aria-label="Começar a usar o Simples.ECG agora"
              >
                <span className="relative z-10 flex items-center">
                  Começar Agora Gratuitamente
                  <span 
                    className="ml-2 transform group-hover:translate-x-1 transition-transform duration-300"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </span>
                <div 
                  className="absolute inset-0 bg-gradient-to-r from-[#064e3b] to-[#065f46] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-3 mb-3 px-4 sm:px-0">
              <div className="flex items-center gap-2">
                <svg className="w-4 sm:w-5 h-4 sm:h-5 text-[#064e3b]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-xs sm:text-sm text-muted-foreground">Sem cartão</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 sm:w-5 h-4 sm:h-5 text-[#064e3b]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-xs sm:text-sm text-muted-foreground">Sem limites</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 sm:w-5 h-4 sm:h-5 text-[#064e3b]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-xs sm:text-sm text-muted-foreground">100% gratuito</span>
              </div>
            </div>

            {/* Características principais - estilo clean */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-8 sm:mt-10 w-full max-w-6xl px-4 sm:px-0">
              {/* Análise Instantânea */}
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-3">
                  <span className="text-4xl sm:text-5xl">⚡</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#064e3b] mb-2">Análise Instantânea</h3>
                <p className="text-sm sm:text-base text-muted-foreground">Resultados em segundos</p>
              </div>
              
              {/* 100% Seguro */}
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-3">
                  <span className="text-4xl sm:text-5xl">🔒</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#064e3b] mb-2">100% Seguro</h3>
                <p className="text-sm sm:text-base text-muted-foreground">Seus dados sempre protegidos</p>
                  </div>
              
              {/* IA Avançada */}
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-3">
                  <span className="text-4xl sm:text-5xl">💡</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#064e3b] mb-2">IA Avançada</h3>
                <p className="text-sm sm:text-base text-muted-foreground">Tecnologia de ponta para suas análises</p>
              </div>
            </div>

            {/* Mobile Optimizations */}
            <style>
              {`
                @media (max-width: 768px) {
                  .hero-section {
                    padding: 0 1rem;
                  }
                  
                  .floating-element {
                    display: none;
                  }
                  
                  /* Touch Improvements */
                  .touch-manipulation {
                    touch-action: manipulation;
                    -webkit-tap-highlight-color: transparent;
                  }
                  
                  /* Larger touch targets */
                  button, 
                  [role="button"],
                  [tabindex="0"] {
                    min-height: 44px;
                    min-width: 44px;
                  }
                  
                  /* Prevent text selection */
                  .no-select {
                    user-select: none;
                    -webkit-user-select: none;
                  }

                  /* Animações mais suaves para mobile */
                  @keyframes pulse-slower {
                    0%, 100% {
                      opacity: 0.5;
                    }
                    50% {
                      opacity: 0.8;
                    }
                  }

                  @keyframes gradient-slower {
                    0% {
                      background-position: 0% 50%;
                    }
                    50% {
                      background-position: 100% 50%;
                    }
                    100% {
                      background-position: 0% 50%;
                    }
                  }

                  .animate-pulse-slower {
                    animation: pulse-slower 6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                  }

                  .animate-gradient-slower {
                    animation: gradient-slower 8s ease infinite;
                    background-size: 200% 200%;
                  }
                }
                
                /* Smooth Scrolling */
                html {
                  scroll-behavior: smooth;
                }
                
                /* Focus Outline */
                :focus {
                  outline: 2px solid #6AB99C;
                  outline-offset: 2px;
                }
                
                /* Remove outline for mouse users */
                :focus:not(:focus-visible) {
                  outline: none;
                }
              `}
            </style>
          </div>

          {/* Footer com Links e Informações */}
          <footer className="mt-auto py-8 sm:py-12 w-full border-t border-primary/10">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                {/* Coluna 1 - Sobre */}
                <div className="space-y-4 text-center">
                  <h3 className="text-base sm:text-lg font-semibold">Sobre a Simples.Health</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Somos uma healthtech que está revolucionando a documentação médica com IA. 
                    Nossa missão é reduzir a burocracia na saúde, permitindo que médicos dediquem mais tempo ao que realmente importa: o cuidado com os pacientes.
                  </p>
                  <a
                    href="https://simples.health"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block"
                  >
                    <img 
                      src="/Logo Escrito.png" 
                      alt="Simples.Health" 
                      className="h-6 sm:h-8 w-auto hover:opacity-80 transition-opacity mx-auto"
                    />
                  </a>
                </div>

                {/* Coluna 2 - Links Úteis */}
                <div className="space-y-4 text-center">
                  <h3 className="text-base sm:text-lg font-semibold">Links Úteis</h3>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/faq" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">
                        Como Funciona
                      </Link>
                    </li>
                    <li>
                      <Link to="/terms" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">
                        Termos de Uso
                      </Link>
                    </li>
                    <li>
                      <Link to="/privacy" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">
                        Política de Privacidade
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* Coluna 3 - Contato */}
                <div className="space-y-4 text-center">
                  <h3 className="text-base sm:text-lg font-semibold">Fale Conosco</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Descubra como a Simples.Health pode transformar sua prática médica, reduzindo em até 80% o tempo gasto com documentação.
                  </p>
                  <a
                    href="mailto:contato@simples.health"
                    className="text-sm sm:text-base text-primary hover:text-primary/80 transition-colors"
                  >
                    contato@simples.health
                  </a>
                </div>
              </div>

              {/* Copyright e Créditos */}
              <div className="pt-6 sm:pt-8 border-t border-primary/10 text-center text-xs sm:text-sm text-muted-foreground">
                <p>
                  © {new Date().getFullYear()} Simples.Health. Todos os direitos reservados.
                </p>
                <p className="mt-2">
                  A Simples.Health está revolucionando a medicina com soluções de IA que reduzem a burocracia e aumentam a eficiência do atendimento médico. 
                  O Simples.ECG é apenas uma das nossas ferramentas gratuitas. Conheça nossa plataforma completa em simples.health
                </p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  )
} 